import { defineStore } from 'pinia';
import { ref } from 'vue';
import { AccountsPayableTotals } from '@/types/api/organisations.types';
import { PaymentTotalResponse } from '@/types/api/accounts-payable.types';
import { OrderTotals } from '@/types/api/orders.types';

export const useTotalsStore = defineStore('totals', () => {
  // Counts
  // Payment Requests
  const paymentRequestsByOrgCount = ref<number>(0);
  const paymentRequestsBySiteCount = ref<number>(0);
  const paymentRequestsByVendorCount = ref<number>(0);
  // Orders
  const ordersByOrgCount = ref<number>(0);
  const ordersBySiteCount = ref<number>(0);
  const ordersByVendorCount = ref<number>(0);
  // Bulk orders
  const bulkOrdersByOrgCount = ref<number>(0);
  // Claims
  const claimsByOrgCount = ref<number>(0);
  const claimsBySiteCount = ref<number>(0);
  const claimsByVendorCount = ref<number>(0);
  // Returns
  const returnsByOrgCount = ref<number>(0);
  const returnsByVendorCount = ref<number>(0);
  // Credits
  const creditsByOrgCount = ref<number>(0);
  const creditsByVendorCount = ref<number>(0);
  // Client Variations
  const clientVariationsByOrgCount = ref<number>(0);

  // Totals
  // Accounts Payable
  const accountsPayableTotalsByOrg = ref<AccountsPayableTotals | null>(null);
  const accountsPayableTotalsByVendor = ref<AccountsPayableTotals | null>(null);
  // Statements
  const statementsTotalPaid = ref<PaymentTotalResponse | null>(null);
  const statementsCurrentPaid = ref<PaymentTotalResponse | null>(null);
  // Payments
  const paymentsTotalPaymentLog = ref<number>(0);
  const paymentsCreditUsed = ref<number>(0);
  const paymentsTotalPaid = ref<number>(0);
  // Orders
  const orderTotalsByOrg = ref<OrderTotals | null>(null);
  const orderTotalsByVendor = ref<OrderTotals | null>(null);
  // Claims
  const claimTotalsByOrg = ref<OrderTotals | null>(null);
  const claimTotalsByVendor = ref<OrderTotals | null>(null);

  const resetCountsAndTotalsInAp = () => {
    paymentRequestsByVendorCount.value = 0;
    ordersByVendorCount.value = 0;
    claimsByVendorCount.value = 0;
    returnsByVendorCount.value = 0;
    creditsByVendorCount.value = 0;
    accountsPayableTotalsByVendor.value = null;
    orderTotalsByVendor.value = null;
    claimTotalsByVendor.value = null;
  };

  const $reset = () => {
    paymentRequestsByOrgCount.value = 0;
    paymentRequestsBySiteCount.value = 0;
    paymentRequestsByVendorCount.value = 0;
    ordersByOrgCount.value = 0;
    ordersBySiteCount.value = 0;
    ordersByVendorCount.value = 0;
    bulkOrdersByOrgCount.value = 0;
    claimsByOrgCount.value = 0;
    claimsBySiteCount.value = 0;
    claimsByVendorCount.value = 0;
    returnsByOrgCount.value = 0;
    returnsByVendorCount.value = 0;
    creditsByOrgCount.value = 0;
    creditsByVendorCount.value = 0;
    clientVariationsByOrgCount.value = 0;
    accountsPayableTotalsByOrg.value = null;
    accountsPayableTotalsByVendor.value = null;
    statementsTotalPaid.value = null;
    statementsCurrentPaid.value = null;
    paymentsTotalPaymentLog.value = 0;
    paymentsCreditUsed.value = 0;
    paymentsTotalPaid.value = 0;
    orderTotalsByOrg.value = null;
    orderTotalsByVendor.value = null;
    claimTotalsByOrg.value = null;
    claimTotalsByVendor.value = null;
  };

  return {
    clientVariationsByOrgCount,
    creditsByOrgCount,
    creditsByVendorCount,
    returnsByOrgCount,
    returnsByVendorCount,
    ordersByOrgCount,
    ordersBySiteCount,
    ordersByVendorCount,
    bulkOrdersByOrgCount,
    claimsByOrgCount,
    claimsBySiteCount,
    claimsByVendorCount,
    paymentRequestsByOrgCount,
    paymentRequestsBySiteCount,
    paymentRequestsByVendorCount,
    accountsPayableTotalsByOrg,
    accountsPayableTotalsByVendor,
    statementsTotalPaid,
    statementsCurrentPaid,
    paymentsTotalPaymentLog,
    paymentsCreditUsed,
    paymentsTotalPaid,
    orderTotalsByOrg,
    orderTotalsByVendor,
    claimTotalsByOrg,
    claimTotalsByVendor,
    $reset,
    resetCountsAndTotalsInAp,
  };
});
